import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { FaBars } from "react-icons/fa";
import { FiShoppingBag, FiSearch, FiX } from "react-icons/fi";
import {
  HeaderWrapper,
  NavContainer,
  ScrollProgress,
  Logo,
  LogoMark,
  LogoText,
  LogoLetter,
  NavMenu,
  NavActions,
  IconButton,
  CartBadge,
  CartCount,
  FlyDotOuter,
  FlyDotInner,
  SplashWrap,
  SplashDrop,
  MobileMenuButton,
  StyledLi,
  StyledSpan,
  MobileMenuOverlay,
  MobileMenuContent,
  MobileMenuHeader,
  MobileMenuLogo,
  MobileMenuClose,
  MobileMenuNav,
  MobileNavLink,
} from "./Header.styles";
import SearchOverlay from "./SearchOverlay";
import { ReactComponent as MrcMark } from "../../assets/icons/logoMrc.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { selectCartTotals } from "../../store/slices/cart/slice";
import { useScrollLock } from "../../hooks/useScrollLock";
import { CART_FLY_EVENT } from "./flyToCart";

const NAV_ITEMS = [
  { label: "Prodavnica", to: "/prodavnica?page=1", match: "prodavnica" },
  { label: "Bendovi", to: "/bendovi?page=1", match: "bendovi" },
  { label: "Novosti", to: "/novosti?page=1", match: "novosti" },
  { label: "O nama", to: "/o-nama", match: "o-nama" },
];

/* Kapljice koje prsnu iz badge-a kad kuglica sleti */
const SPLASH_DROPS = Array.from({ length: 7 }, (_, i) => {
  const angle = (i / 7) * Math.PI * 2 - Math.PI / 2 + 0.3;
  const dist = 14 + (i % 3) * 5;
  return {
    sx: Math.cos(angle) * dist,
    sy: Math.sin(angle) * dist,
    delay: (i % 3) * 40,
  };
});

const Header = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { totalItems } = useSelector(selectCartTotals);

  // Fly-to-cart animacija: prikazani count je "zamrznut" dok kuglica leti,
  // pa se na sletanju animirano podigne uz prskanje kapljica
  const cartBtnRef = useRef(null);
  const totalItemsRef = useRef(totalItems);
  totalItemsRef.current = totalItems;

  const [displayCount, setDisplayCount] = useState(totalItems);
  const [flights, setFlights] = useState([]);
  const [burstKey, setBurstKey] = useState(0);
  const [splashing, setSplashing] = useState(false);
  // Logo spin, pokreće se na hover, ali se uvek odvrti do kraja (bez snap-a)
  const [logoSpin, setLogoSpin] = useState(false);
  const logoSpinTimer = useRef(null);
  const triggerLogoSpin = useCallback(() => {
    setLogoSpin(true);
    clearTimeout(logoSpinTimer.current);
    // Reset tek kad se animacija (0.7s) završi, spin se uvek odvrti ceo
    logoSpinTimer.current = setTimeout(() => setLogoSpin(false), 760);
  }, []);
  const pendingFlights = useRef(0);
  const flightIdRef = useRef(0);

  useEffect(() => {
    if (pendingFlights.current === 0) {
      setDisplayCount(totalItems);
    }
  }, [totalItems]);

  useEffect(() => {
    const onFly = (e) => {
      const { x, y } = e.detail || {};
      const target = cartBtnRef.current?.getBoundingClientRect();
      if (typeof x !== "number" || typeof y !== "number" || !target) return;
      pendingFlights.current += 1;
      flightIdRef.current += 1;
      setFlights((prev) => [
        ...prev,
        {
          id: flightIdRef.current,
          x,
          y,
          // Cilj: centar count badge-a (gornji desni ugao dugmeta korpe)
          dx: target.right - 9 - x,
          dy: target.top + 10 - y,
        },
      ]);
    };

    window.addEventListener(CART_FLY_EVENT, onFly);
    return () => window.removeEventListener(CART_FLY_EVENT, onFly);
  }, []);

  const onFlightEnd = (id) => {
    setFlights((prev) => prev.filter((flight) => flight.id !== id));
    pendingFlights.current = Math.max(0, pendingFlights.current - 1);
    setDisplayCount(totalItemsRef.current);
    setBurstKey((key) => key + 1);
    setSplashing(true);
  };

  // Scroll progress linija, širina se upisuje direktno u style preko ref-a
  // (bez setState-a) da skrol ne izaziva re-render celog headera
  const progressRef = useRef(null);

  useEffect(() => {
    let ticking = false;

    const update = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${progress})`;
      }
      ticking = false;
    };

    const onScrollOrResize = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, []);

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Globalna pretraga
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Poboljšano scroll state management
  const [scrollState, setScrollState] = useState({
    isVisible: true,
    lastScrollY: 0,
    scrollDirection: "up",
    isAnimating: false,
    isAtTop: true,
    isNavigating: false,
  });

  // Poboljšana scroll logika
  const handleScroll = useCallback(() => {
    // Sprečava animacije tokom navigacije
    if (scrollState.isNavigating) {
      return;
    }

    const currentScrollY = window.scrollY;
    const isAtTop = currentScrollY <= 20; // Smatraj da je na vrhu ako je scroll <= 20px
    const scrollDifference = Math.abs(currentScrollY - scrollState.lastScrollY);

    // Ignore small scroll movements
    if (scrollDifference < 8) {
      return;
    }

    const scrollDirection =
      currentScrollY > scrollState.lastScrollY ? "down" : "up";

    if (
      isAtTop !== scrollState.isAtTop ||
      scrollDirection !== scrollState.scrollDirection
    ) {
      setScrollState((prevState) => ({
        ...prevState,
        isVisible: true,
        isAtTop,
        lastScrollY: currentScrollY,
        scrollDirection,
        isAnimating: false,
      }));
    } else {
      setScrollState((prevState) => ({
        ...prevState,
        lastScrollY: currentScrollY,
      }));
    }
  }, [
    scrollState.lastScrollY,
    scrollState.scrollDirection,
    scrollState.isNavigating,
    scrollState.isAtTop,
  ]);

  // Optimizovani scroll effect sa throttling
  useEffect(() => {
    let ticking = false;

    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", throttledHandleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
    };
  }, [handleScroll]);

  // Initialize proper state on component mount
  useEffect(() => {
    const currentScrollY = window.scrollY;
    const isAtTop = currentScrollY <= 20;

    setScrollState((prevState) => ({
      ...prevState,
      isVisible: true,
      isAtTop,
      lastScrollY: currentScrollY,
      isAnimating: false,
      isNavigating: false,
    }));
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Block scrolling when mobile menu is open
  useScrollLock(isMobileMenuOpen);

  const onNavigate = (to) => {
    // Close mobile menu when navigating
    closeMobileMenu();

    // Postavi flag za navigaciju da spreciš animacije
    setScrollState((prevState) => ({
      ...prevState,
      isNavigating: true,
      isVisible: true,
    }));

    navigate(to);

    // Scroll to top immediately without smooth behavior
    setTimeout(() => {
      window.scrollTo(0, 0);

      setTimeout(() => {
        setScrollState((prevState) => ({
          ...prevState,
          isNavigating: false,
          isAtTop: true,
          lastScrollY: 0,
          isAnimating: false,
        }));
      }, 100);
    }, 50);
  };

  return (
    <>
      <HeaderWrapper $isAtTop={scrollState.isAtTop}>
        <NavContainer>
          <Logo
            onClick={() => onNavigate("/")}
            onMouseEnter={triggerLogoSpin}
            aria-label="МРЧ, početna"
          >
            <LogoMark aria-hidden="true">
              <MrcMark className={logoSpin ? "spin" : undefined} />
            </LogoMark>
            <LogoText aria-hidden="true">
              {["М", "Р", "Ч"].map((letter, i) => (
                <LogoLetter key={letter} style={{ "--i": i }}>
                  <span>{letter}</span>
                </LogoLetter>
              ))}
            </LogoText>
          </Logo>

          <NavMenu>
            {NAV_ITEMS.map((item, i) => (
              <StyledLi
                key={item.match}
                style={{ "--i": i }}
                onClick={() => onNavigate(item.to)}
              >
                <StyledSpan $active={pathname.includes(item.match)}>
                  {item.label}
                </StyledSpan>
              </StyledLi>
            ))}
          </NavMenu>

          <NavActions>
            <IconButton
              onClick={() => setIsSearchOpen(true)}
              aria-label="Pretraga"
              title="Pretraga"
            >
              <FiSearch />
            </IconButton>
            <IconButton
              ref={cartBtnRef}
              onClick={() => onNavigate("/korpa")}
              aria-label="Korpa"
              title="Korpa"
            >
              <FiShoppingBag />
              {displayCount > 0 && (
                <CartBadge key={`badge-${burstKey}`} $pop={burstKey > 0}>
                  <CartCount key={displayCount}>{displayCount}</CartCount>
                </CartBadge>
              )}
              {splashing && (
                <SplashWrap
                  key={`splash-${burstKey}`}
                  onAnimationEnd={() => setSplashing(false)}
                >
                  {SPLASH_DROPS.map((drop, i) => (
                    <SplashDrop
                      key={i}
                      style={{
                        "--sx": `${drop.sx}px`,
                        "--sy": `${drop.sy}px`,
                        animationDelay: `${drop.delay}ms`,
                      }}
                    />
                  ))}
                </SplashWrap>
              )}
            </IconButton>
            <MobileMenuButton
              onClick={toggleMobileMenu}
              aria-label="Meni"
              title="Meni"
            >
              <FaBars size={16} />
            </MobileMenuButton>
          </NavActions>
        </NavContainer>
        <ScrollProgress ref={progressRef} aria-hidden="true" />
      </HeaderWrapper>

      {/* Fly-to-cart kuglice, portal da transform/blur predaka ne pomeri fixed */}
      {flights.map((flight) =>
        createPortal(
          <FlyDotOuter
            key={flight.id}
            style={{
              left: flight.x,
              top: flight.y,
              "--fly-dx": `${flight.dx}px`,
            }}
            onAnimationEnd={(e) => {
              if (e.target === e.currentTarget) onFlightEnd(flight.id);
            }}
          >
            <FlyDotInner style={{ "--fly-dy": `${flight.dy}px` }} />
          </FlyDotOuter>,
          document.body,
        ),
      )}

      {/* Globalna pretraga */}
      <SearchOverlay open={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

      {/* Mobile Menu Overlay */}
      <MobileMenuOverlay $isOpen={isMobileMenuOpen}>
        <MobileMenuContent $isOpen={isMobileMenuOpen}>
          <MobileMenuHeader>
            <MobileMenuLogo onClick={() => onNavigate("/")}>
              <MrcMark aria-hidden="true" />
              МРЧ
            </MobileMenuLogo>
            <MobileMenuClose onClick={closeMobileMenu} aria-label="Zatvori">
              <FiX />
            </MobileMenuClose>
          </MobileMenuHeader>

          <MobileMenuNav>
            {NAV_ITEMS.map((item) => (
              <MobileNavLink
                key={item.match}
                $active={pathname.includes(item.match)}
                onClick={() => onNavigate(item.to)}
              >
                {item.label}
              </MobileNavLink>
            ))}
          </MobileMenuNav>
        </MobileMenuContent>
      </MobileMenuOverlay>
    </>
  );
};

export default Header;
